import { Box, Button, Chip } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Locale, LocalizedString } from '@synergy/models';
import { Localized, PoolFormattedMessage } from '@synergy/components';
import { PaloColumn, PaloConfirmationDialog, PaloListView, PaloRowAction, PaloThumb } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as NewsPostActions from '../store/news-post-actions';
import * as NewsPostListActions from '../news-post-list/store/news-post-list-actions';
import * as NewsPostListSelectors from '../news-post-list/store/news-post-list-selectors';
import { NewsPost } from '../models/news-post';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const PaloNewsPostList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('news-posts');
    const columnFilter = usePaloColumnFilters('news-posts');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('createdAt', 'desc');
    const requesting = useAppSelector(NewsPostListSelectors.newsPostListRequestingSelector);
    const paginated = useAppSelector(NewsPostListSelectors.newsPostListItemsSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const [toDelete, setToDelete] = useState<number | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(NewsPostListActions.fetchNewsPostList(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (post: NewsPost): void =>
        navigate(ROUTES.contentManagement.poolNews.detail.replace(':id', post.id.toString()));

    const missingTranslations = (title: LocalizedString, text: LocalizedString): Locale[] =>
        availableLocales.filter(locale => !title[locale] && !text[locale]);

    const handleDelete = (): void => {
        if (toDelete) {
            dispatch(NewsPostActions.deleteNewsPost(toDelete));
            setToDelete(null);
        }
    };

    const columns: PaloColumn<NewsPost>[] = [
        {
            key: 'picture',
            type: 'icon',
            head: <FormattedMessage id="app.table.column.picture" />,
            cell: post => <PaloThumb src={post.previewImage} width={50} height={26} />,
        },
        {
            key: 'title',
            type: 'name',
            sortKey: 'title',
            head: <FormattedMessage id="app.table.column.title" />,
            cell: post => <Localized value={post.title} />,
        },
        {
            key: 'publishAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishAt" />,
            cell: post => <FormattedDate value={post.publishAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'createdAt',
            type: 'date',
            sortKey: 'createdAt',
            head: <FormattedMessage id="app.table.column.createdAt" />,
            cell: post => <FormattedDate value={post.createdAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: post => {
                const missing = missingTranslations(post.title, post.text);

                return (
                    <Chip
                        size="small"
                        color={missing.length === 0 ? 'success' : 'error'}
                        label={
                            missing.length === 0 ? (
                                <FormattedMessage id="app.common.translated" />
                            ) : (
                                <FormattedMessage
                                    id="app.common.missingTranslations"
                                    values={{ value: missing.join(', ') }}
                                />
                            )
                        }
                    />
                );
            },
        },
    ];

    const rowActions = (post: NewsPost): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(post) },
        { label: 'Löschen', onClick: () => setToDelete(post.id) },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${ROUTES.contentManagement.poolNews.list}/new`)}
        >
            <FormattedMessage id="app.contentManagement.newPoolNews" />
        </Button>
    );

    return (
        <PaloListView<NewsPost>
            pageTitle={intl.formatMessage({ id: 'app.contentManagement.poolNews' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            title={<FormattedMessage id="app.contentManagement.poolNews" />}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="news-posts" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={post => post.id}
            requesting={requesting}
            emptyLabel="Keine Beiträge."
            onRowClick={openDetail}
            rowActions={rowActions}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <PaloConfirmationDialog
                isVisible={!!toDelete}
                onConfirm={handleDelete}
                onCancel={() => setToDelete(null)}
                title={<FormattedMessage id="app.contentManagement.newsPost.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.contentManagement.newsPost.deleteWarning"
                    values={{
                        name: getLocalizedString(
                            availableLocales,
                            paginated.items.find(item => item.id === toDelete)?.title
                        ),
                    }}
                />
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
